/**
 * O retrato do monstro (techspec-hud-v3 §09), comum à Wiki e às Caçadas: os quadros da folha do próprio
 * jogo, da direção que olha para quem vê, recortados no contorno e centrados num quadrado (o mesmo
 * enquadramento para todos os quadros, de pé no chão). Cache por aparência e ação; a folha já está no
 * cache do jogo. Falhou (aparência sem a ação, rede): lista vazia, e quem mostra deixa o espaço vazio.
 */
import { lerJsonDaFolha } from './spriteAtlas';

type Meta = { celula: [number, number]; quadros?: number; direcoes: { linha: number; espelhar?: boolean }[] };

const cache = new Map<string, Promise<string[]>>();

export function quadrosDoRetrato(aparencia: string, acao: 'idle' | 'attack' = 'idle'): Promise<string[]> {
  const ap = aparencia.replace(/[^a-z0-9-]/g, '');
  const k = `${ap}/${acao}`;
  let p = cache.get(k);
  if (p) return p;
  p = (async () => {
    const base = `/sprites/enemies/${ap}/${acao}/${ap}-${acao}`;
    const { raw, marca } = await lerJsonDaFolha(`${base}.json`);
    const meta = raw as unknown as Meta;
    const linha = meta.direcoes.find(d => !d.espelhar)?.linha ?? 0;
    const img = new Image();
    img.src = `${base}.png?v=${marca}`;
    await img.decode();
    const [cw, ch] = meta.celula;
    const n = Math.max(1, meta.quadros ?? Math.floor(img.naturalWidth / cw));
    const cel = document.createElement('canvas');
    cel.width = cw;
    cel.height = ch;
    const g = cel.getContext('2d', { willReadFrequently: true })!;
    const caixas: { x0: number; y0: number; w: number; h: number; q: number }[] = [];
    for (let q = 0; q < n; q++) {
      g.clearRect(0, 0, cw, ch);
      g.drawImage(img, q * cw, linha * ch, cw, ch, 0, 0, cw, ch);
      const px = g.getImageData(0, 0, cw, ch).data;
      let x0 = cw, y0 = ch, x1 = -1, y1 = -1;
      for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) {
        if (px[(y * cw + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; }
      }
      if (x1 >= x0) caixas.push({ x0, y0, w: x1 - x0 + 1, h: y1 - y0 + 1, q });
    }
    if (!caixas.length) return [];
    const lado = Math.max(...caixas.map(c => Math.max(c.w, c.h))) + 4;
    return caixas.map(c => {
      const r = document.createElement('canvas');
      r.width = lado;
      r.height = lado;
      r.getContext('2d')!.drawImage(img, c.q * cw + c.x0, linha * ch + c.y0, c.w, c.h, Math.floor((lado - c.w) / 2), lado - c.h - 2, c.w, c.h);
      return r.toDataURL();
    });
  })().catch(() => []);
  cache.set(k, p);
  return p;
}

const aparados = new Map<string, Promise<string>>();

/**
 * Ícone recortado no contorno e centrado num quadrado (o `aparar` do protótipo): na grade de 34 px o
 * desenho ocupa a casa. Falhou (SVG sem tamanho, rede): devolve o próprio endereço.
 */
export function iconeAparado(src: string): Promise<string> {
  let p = aparados.get(src);
  if (p) return p;
  p = (async () => {
    const img = new Image();
    img.src = src;
    await img.decode();
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return src;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(img, 0, 0);
    const px = g.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (px[(y * w + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (y < y0) y0 = y; if (x > x1) x1 = x; if (y > y1) y1 = y; }
    }
    if (x1 < x0) return src;
    const cw = x1 - x0 + 1, ch = y1 - y0 + 1, lado = Math.max(cw, ch) + 2;
    const r = document.createElement('canvas');
    r.width = lado;
    r.height = lado;
    r.getContext('2d')!.drawImage(c, x0, y0, cw, ch, Math.floor((lado - cw) / 2), Math.floor((lado - ch) / 2), cw, ch);
    return r.toDataURL();
  })().catch(() => src);
  aparados.set(src, p);
  return p;
}
